import { useState } from 'react';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import {
  Area,
  Bar,
  Box,
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  ChartTooltipTimestampHeader,
  Divider,
  Stack,
  TimeXAxis,
  tokens,
  ToggleButton,
  ToggleButtonGroup,
  YAxis,
} from '@cast/design-system';
import {
  ChartMetric,
  ChartMetricsRowSkeleton,
} from '@cast/features-cluster-ui';
import { FailedToLoad } from '@cast/shared-messages-ui';
import { formatCurrency } from '@cast/utils';

import { DateRangeUnsupportedBanner } from '../_components/DateRangeUnsupportedBanner';
import { WidgetLayout } from '../../../_components/ui/WidgetLayout';

import {
  type WorkloadComputeSpendChartPoint,
  useWorkloadComputeSpendChartData,
} from './_hooks/useWorkloadComputeSpendChartData';

dayjs.extend(utc);

const TOGGLE_OPTIONS = {
  daily: 'daily',
  cumulative: 'cumulative',
} as const;

type ComputeSpendMode = (typeof TOGGLE_OPTIONS)[keyof typeof TOGGLE_OPTIONS];

type SeriesKey = 'onDemand' | 'spot' | 'fallback' | 'storage';

const SERIES_CONFIG: Record<
  SeriesKey,
  { label: string; fill: string; color: string }
> = {
  onDemand: {
    label: 'On-demand',
    fill: 'blue.300',
    color: tokens.theme.data.blue300,
  },
  fallback: {
    label: 'Fallback',
    fill: 'yellow.300',
    color: tokens.theme.data.yellow300,
  },
  spot: {
    label: 'Spot',
    fill: 'blue.800',
    color: tokens.theme.data.blue800,
  },
  storage: {
    label: 'Storage',
    fill: 'aqua.300',
    color: tokens.theme.data.aqua300,
  },
};

export const ComputeSpendCard = () => {
  const [mode, setMode] = useState<ComputeSpendMode>(TOGGLE_OPTIONS.daily);
  const [visibleSeries, setVisibleSeries] = useState<
    Record<SeriesKey, boolean>
  >({
    onDemand: true,
    spot: true,
    fallback: true,
    storage: true,
  });

  const {
    dailyData,
    cumulativeData,
    totals,
    isLoading,
    isFetching,
    error,
    refetch,
    timeZone,
    isRangeSupported,
  } = useWorkloadComputeSpendChartData();

  const toggleSeries = (key: SeriesKey) => {
    setVisibleSeries((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const hasError = !!error;
  const isCardLoading = isLoading || (hasError && isFetching);

  const chartData = mode === TOGGLE_OPTIONS.daily ? dailyData : cumulativeData;
  const ChartElement = mode === TOGGLE_OPTIONS.daily ? Bar : Area;

  const hasNoData = chartData.every(
    (point) =>
      point.onDemand === 0 &&
      point.spot === 0 &&
      point.fallback === 0 &&
      point.storage === 0
  );

  const formatMetric = (value: number) =>
    formatCurrency(value, { maximumFractionDigits: 2 });

  const headerEndContent = (
    <ToggleButtonGroup
      value={mode}
      onChange={(_, value) => {
        if (value) {
          setMode(value as ComputeSpendMode);
        }
      }}
      size="sm"
      testId="compute-spend-mode-toggle"
    >
      <ToggleButton
        value={TOGGLE_OPTIONS.daily}
        text="Daily cost"
        testId="compute-spend-daily"
      />
      <ToggleButton
        value={TOGGLE_OPTIONS.cumulative}
        text="Cumulative"
        testId="compute-spend-cumulative"
      />
    </ToggleButtonGroup>
  );

  const renderSeries = (key: SeriesKey) => {
    if (!visibleSeries[key]) {
      return null;
    }

    const forecastProps =
      ChartElement === Bar
        ? {
            getBarPropsByPoint: (point: WorkloadComputeSpendChartPoint) => ({
              fillOpacity: point.forecast ? 0.3 : 1,
            }),
          }
        : {
            getAreaPropsByPoint: (point: WorkloadComputeSpendChartPoint) => ({
              fillOpacity: point.forecast ? 0.3 : 1,
              strokeOpacity: point.forecast ? 0.3 : 1,
            }),
          };

    return (
      <ChartElement<WorkloadComputeSpendChartPoint>
        key={key}
        dataKey={key}
        stackId="compute-spend"
        fill={SERIES_CONFIG[key].fill}
        tooltipConfig={{
          displayName: SERIES_CONFIG[key].label,
        }}
        {...forecastProps}
      />
    );
  };

  return (
    <WidgetLayout
      title="Compute spend"
      testId="compute-spend-card"
      headerEndContent={headerEndContent}
    >
      {!isRangeSupported ? (
        <DateRangeUnsupportedBanner testId="compute-spend-unsupported-range" />
      ) : hasError && !isFetching ? (
        <FailedToLoad title="Failed to load compute spend" refresh={refetch} />
      ) : (
        <Stack gap={16} height="100%">
          {isCardLoading ? (
            <ChartMetricsRowSkeleton
              alwaysRow
              gap={16}
              count={5}
              testId="compute-spend-metrics-skeleton"
            />
          ) : (
            <Stack direction="row" gap={16} alignItems="center" flexWrap="wrap">
              <ChartMetric
                testId="compute-spend-total"
                size="m"
                label="Total"
                value={formatMetric(totals.total)}
              />
              <Divider orientation="vertical" flexItem />
              <ChartMetric
                testId="compute-spend-on-demand"
                size="m"
                label="On-demand"
                color={SERIES_CONFIG.onDemand.color}
                value={formatMetric(totals.onDemand)}
                disabled={!visibleSeries.onDemand}
                onClick={() => toggleSeries('onDemand')}
              />
              <Divider orientation="vertical" flexItem />
              <ChartMetric
                testId="compute-spend-fallback"
                size="m"
                label="Fallback"
                color={SERIES_CONFIG.fallback.color}
                value={formatMetric(totals.fallback)}
                disabled={!visibleSeries.fallback}
                onClick={() => toggleSeries('fallback')}
              />
              <Divider orientation="vertical" flexItem />
              <ChartMetric
                testId="compute-spend-spot"
                size="m"
                label="Spot"
                color={SERIES_CONFIG.spot.color}
                value={formatMetric(totals.spot)}
                disabled={!visibleSeries.spot}
                onClick={() => toggleSeries('spot')}
              />
              <Divider orientation="vertical" flexItem />
              <ChartMetric
                testId="compute-spend-storage"
                size="m"
                label="Storage"
                color={SERIES_CONFIG.storage.color}
                value={formatMetric(totals.storage)}
                disabled={!visibleSeries.storage}
                onClick={() => toggleSeries('storage')}
              />
            </Stack>
          )}
          <Box flex={1} minHeight={0}>
            <ChartContainer<WorkloadComputeSpendChartPoint>
              data={chartData}
              isLoading={isCardLoading}
              hasNoData={hasNoData}
              sx={{ height: '100%' }}
              barConfig={{ barSize: 16, maxBarSize: 16 }}
            >
              <ChartGrid hideVertical />
              <TimeXAxis<WorkloadComputeSpendChartPoint>
                unixDataKey="chartTimestampUnix"
                timezone={timeZone}
              />
              <YAxis<WorkloadComputeSpendChartPoint>
                tickFormatter={(value) => `$${value}`}
                allowDecimals
                tickCount={3}
                padding={{ top: 12 }}
              />
              {renderSeries('onDemand')}
              {renderSeries('fallback')}
              {renderSeries('spot')}
              {renderSeries('storage')}
              <ChartTooltip<WorkloadComputeSpendChartPoint>
                testId="compute-spend-tooltip"
                header={(headerProps) => (
                  <ChartTooltipTimestampHeader
                    data={{ timestamp: headerProps.data.timestamp ?? '' }}
                    timezone={timeZone}
                    testId={headerProps.testId}
                  />
                )}
                formatValue={(value) =>
                  formatCurrency(Number(value), { maximumFractionDigits: 2 })
                }
              />
            </ChartContainer>
          </Box>
        </Stack>
      )}
    </WidgetLayout>
  );
};
